import { Box, Typography, Button, Stack } from "@mui/material";
import { RouteLink } from "@/components/common/styles";
import { useMatchRoute } from "@tanstack/react-router";

import HelpArticle from "../buttons/HelpArticle";
import { Header } from "./commonStyles";
import { ColoredCard } from "@/ui/ColoredCard";
import { useMemo, useState } from "react";
import useNeedsAccountVerification from "@/hooks/useNeedsAccountVerification";
import { Warning } from "@mui/icons-material";
import InfoCardDialog from "@/ui/dialogs/InfoCardDialog";
import { getObjectFromStorage, setToStorage } from "@/utils/localStorageUtils";

export function useShouldShowAccountVerificationBanner() {
  const matchRoute = useMatchRoute();
  const { flows } = useNeedsAccountVerification();
  const shouldHideBanner =
    !!matchRoute({ to: "/profile" }) ||
    !!matchRoute({ to: "/accountverification" }) ||
    !!matchRoute({ to: "/subscription/customize" }) ||
    !!matchRoute({ to: "/subscription/customize/checkout" }) ||
    !flows?.length;

  return !shouldHideBanner;
}

function AccountVerificationBannerImplementation() {
  const [isModalOpen, setModalOpen] = useState(true);
  const {
    needsEvidence,
    needsIdVerification,
    hasUnansweredQuestions,
    shouldRetryIdVerification,
  } = useNeedsAccountVerification();

  const shouldHideBanner = !useShouldShowAccountVerificationBanner();

  const type =
    needsEvidence ||
    hasUnansweredQuestions ||
    needsIdVerification ||
    shouldRetryIdVerification
      ? "error"
      : "warning";

  const { title, body, buttonText } = useMemo(() => {
    if (hasUnansweredQuestions)
      return {
        title: "Additional Information Required",
        body: "Please answer some questions about your activity.",
        buttonText: "Submit Additional Info",
      };
    if (needsEvidence)
      return {
        title: "Your account requires verification",
        body: "Please complete verification to keep your service active. If verification is not completed, or activity in flagged target categories continues or increases, your proxies may be limited and your account may be temporarily suspended.",
        buttonText: "Verify Account",
      };

    if (shouldRetryIdVerification) {
      return {
        title: "ID Verification Failed",
        body: "Please retry verification to avoid any interruptions on proxy activities.",
        buttonText: "Retry Verification",
      };
    }

    if (needsIdVerification)
      return {
        title: "ID Verification Required",
        body: "Please verify your ID.",
        buttonText: "Verify ID",
      };
    return {
      title: "Pending Verification",
      body: "We have received your reply and will get back to you within 2 business days.",
      buttonText: "",
    };
  }, [
    hasUnansweredQuestions,
    needsEvidence,
    needsIdVerification,
    shouldRetryIdVerification,
  ]);

  return shouldHideBanner ? null : (
    <ColoredCard type={type}>
      <Box sx={{ px: 3, py: 2 }}>
        <Stack
          direction="row"
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Stack direction="row" alignItems={"center"} spacing={3}>
            {buttonText === "Retry Verification" ? (
              <Box>
                <Warning color="error" />
              </Box>
            ) : null}
            <Box>
              <Header
                variant="body1"
                fontWeight="bold"
                type={type}
                sx={{ display: "flex", alignItems: "center" }}
              >
                {title}
                <HelpArticle id="pendingVerification" />
              </Header>
              <Typography variant="body2">{body}</Typography>
            </Box>
          </Stack>

          {type === "error" ? (
            <RouteLink to="/accountverification">
              <Button variant="contained" color="error">
                {buttonText}
              </Button>
            </RouteLink>
          ) : null}
        </Stack>
      </Box>

      <InfoCardDialog
        open={
          buttonText === "Retry Verification" &&
          !getObjectFromStorage("hasSeenIdVerificationRetryModal") &&
          isModalOpen
        }
        onClose={() => {
          setModalOpen(false);
          setToStorage("hasSeenIdVerificationRetryModal", "true");
        }}
        imageAlt="error"
        imageSrc="/error"
        title="ID Verification Failed"
        description="Please retry verification to avoid any interruptions on proxy activities."
      >
        <RouteLink to="/accountverification">
          <Button
            variant="contained"
            data-testid="verify-account-button"
            onClick={() => {
              setToStorage("hasSeenIdVerificationRetryModal", "true");
            }}
          >
            Retry Verification
          </Button>
        </RouteLink>
      </InfoCardDialog>
    </ColoredCard>
  );
}

// function AccountVerificationBannerNewExperienceImplementation() {
//   return null;
// }

export default function AccountVerificationBanner() {
  return <AccountVerificationBannerImplementation />;
}
